import React, { memo, useState } from 'react';
import * as Styles from './SshKeysList.styles';
import {
  IconCalendar, Tooltip, IconCopyFile, IconTrashBg, IconKeyV2, IconWarning, Button, IconAdd,
} from 'gatorcomponents';

import useLocale from '@/hooks/useLocale';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

import { formatDateTime } from '@/utils/Formatters/formatDateTime';

import EmptyState from './components/EmptyState';
import DeleteKeyModal from '../DeleteKeyModal';

import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

const SshKeysList = memo(({
  sshKeysData,
  error,
  loading,
  onOpenModalCreateKey,
}) => {
  const dispatch = useDispatch();
  const { id: hostingId } = useParams();

  const [openModalDeleteKey, setOpenModalDeleteKey] = useState(false);
  const [selectedSshKey, setSelectedSshKey] = useState(null);

  const isMobile = useIsMobile();
  const copyToClipboard = useCopyToClipboard();

  const {
    keys: sshKeysList = [],
    maxKeys: sshKeysMax = 5,
  } = sshKeysData || {};

  const sshKeysCount = sshKeysList.length || 0;

  const hasSshKeys = Boolean(sshKeysCount);
  const hasError = Boolean(error);
  const isLoading = Boolean(loading);

  const isEmpty = !hasSshKeys && !isLoading && !hasError;
  const isError = hasError && !isLoading;

  const { productDetails: productDetailsLocale } = useLocale();
  const { sshKeys: sshKeysLocale } = productDetailsLocale.vpsOci;

  const handleCreateKey = () => {
    onOpenModalCreateKey && onOpenModalCreateKey();
  };

  const handleRefreshPage = () => {
    dispatch(vpsOciActions.sshKeys.fetch.request(hostingId));
  };

  const handleOpenModalDeleteKey = (sshKey) => {
    setOpenModalDeleteKey(true);
    setSelectedSshKey(sshKey);
  };

  const handleCloseModalDeleteKey = () => {
    setOpenModalDeleteKey(false);
  };

  if (isEmpty) {
    return (
      <EmptyState
        icon={<IconKeyV2 color="lowPure" size="md" />}
        locale={sshKeysLocale.management.list.fallbacks.empty}
        testId="ssh-keys-list-empty-state"
        button={(
          <Button
            label={sshKeysLocale.management.list.fallbacks.empty.createKeyButton}
            onClick={handleCreateKey}
            variant="secondary"
            width={isMobile ? 'full' : 'fit'}
            size="large"
            version="v2"
            iconLeft={<IconAdd color="primaryMedium" />}
            testId="ssh-keys-list-empty-state-create-key-button"
          />
        )}
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<IconWarning color="lowPure" size="md" />}
        locale={sshKeysLocale.management.list.fallbacks.error}
        testId="ssh-keys-list-error-state"
        button={(
          <Button
            label={sshKeysLocale.management.list.fallbacks.error.refreshPageButton}
            onClick={handleRefreshPage}
            variant="primary"
            width={isMobile ? 'full' : 'fit'}
            size="large"
            version="v2"
            testId="ssh-keys-error-state-refresh-page-button"
          />
        )}
      />
    );
  }

  return (
    <Styles.Container data-testid="ssh-keys-list">
      <Styles.Title data-testid="ssh-keys-list-title">
        {sshKeysLocale.management.list.title}
        <Styles.Count data-testid="ssh-keys-list-count">
          {sshKeysLocale.management.list.sshKeysCount(sshKeysCount, sshKeysMax)}
        </Styles.Count>
      </Styles.Title>
      <Styles.Items data-testid="ssh-keys-list-items">
        {sshKeysList.map(sshKey => (
          <Styles.Item
            data-testid={`ssh-keys-item-${sshKey.id}`}
            key={sshKey.id}
          >
            <Styles.ItemInfo>
              <Styles.ItemName>{sshKey.name}</Styles.ItemName>
              <Styles.ItemCreatedAt>
                <IconCalendar color="lowPure" size="sm2" />
                {sshKeysLocale.management.list.item.createdAt(formatDateTime(sshKey.createdAt), isMobile)}
              </Styles.ItemCreatedAt>
            </Styles.ItemInfo>
            <Styles.ItemActions>
              <Styles.ItemActionButton
                data-testid={`ssh-keys-item-copy-button-${sshKey.id}`}
                onClick={() => copyToClipboard(sshKey.key)}
              >
                <Tooltip content={sshKeysLocale.management.list.item.buttons.copy} preferredPosition="right">
                  <IconCopyFile color="primaryMedium" size="md" />
                </Tooltip>
              </Styles.ItemActionButton>
              <Styles.ItemActionButton
                data-testid={`ssh-keys-item-delete-button-${sshKey.id}`}
                onClick={() => handleOpenModalDeleteKey(sshKey)}
              >
                <Tooltip
                  content={sshKeysLocale.management.list.item.buttons.delete}
                  preferredPosition="right"
                  data-testid={`ssh-keys-item-delete-button-tooltip-${sshKey.id}`}
                >
                  <IconTrashBg color="primaryMedium" secondaryColor="transparent" size="md" />
                </Tooltip>
              </Styles.ItemActionButton>
            </Styles.ItemActions>
          </Styles.Item>
        ))}
      </Styles.Items>

      {openModalDeleteKey && (
        <DeleteKeyModal
          onClose={handleCloseModalDeleteKey}
          selectedSshKey={selectedSshKey}
        />
      )}
    </Styles.Container>
  );
});

export default SshKeysList;
